export class ObjectPool<T> {
  private pool: T[] = [];
  private createFn: () => T;
  private resetFn: (obj: T) => void;
  private activeFn?: (obj: T) => void;
  private maxSize: number;
  private currentSize: number = 0;

  constructor(
    createFn: () => T,
    resetFn: (obj: T) => void,
    maxSize: number = 50,
    activeFn?: (obj: T) => void
  ) {
    this.createFn = createFn;
    this.resetFn = resetFn;
    this.maxSize = maxSize;
    this.activeFn = activeFn;
  }

  // Get an object from the pool
  get(): T {
    let obj: T;
    
    if (this.pool.length > 0) {
      obj = this.pool.pop()!;
    } else {
      obj = this.createFn();
      this.currentSize++;
    }
    
    // Activate the object if needed
    if (this.activeFn) {
      this.activeFn(obj);
    }
    
    return obj;
  }

  // Return an object to the pool
  release(obj: T): void {
    if (!obj) return;
    
    // Reset the object state
    this.resetFn(obj);
    
    // Add back to pool if not at capacity
    if (this.pool.length < this.maxSize) {
      this.pool.push(obj);
    } else {
      // Pool is full, destroy the object
      this.currentSize--;
      if ((obj as any).destroy) {
        (obj as any).destroy();
      }
    }
  }

  // Preload the pool with objects
  preload(count: number): void {
    const preloadCount = Math.min(count, this.maxSize);
    for (let i = 0; i < preloadCount; i++) {
      const obj = this.createFn();
      this.resetFn(obj);
      this.pool.push(obj);
      this.currentSize++;
    }
  }

  // Get pool statistics
  getStats(): { poolSize: number; totalCreated: number; maxSize: number } {
    return {
      poolSize: this.pool.length,
      totalCreated: this.currentSize,
      maxSize: this.maxSize,
    };
  }

  // Clear the entire pool
  clear(): void {
    this.pool.forEach(obj => {
      if ((obj as any).destroy) {
        (obj as any).destroy();
      }
    });
    this.pool = [];
    this.currentSize = 0;
  }

  // Resize the pool
  resize(newMaxSize: number): void {
    this.maxSize = newMaxSize;
    
    // If the new size is smaller, remove excess objects
    while (this.pool.length > newMaxSize) {
      const obj = this.pool.pop();
      if (obj && (obj as any).destroy) {
        (obj as any).destroy();
      }
      this.currentSize--;
    }
  }
}

// Global pool manager for common game objects
export class PoolManager {
  private static instance: PoolManager;
  private pools: Map<string, ObjectPool<any>> = new Map();

  private constructor() {}

  public static getInstance(): PoolManager {
    if (!PoolManager.instance) {
      PoolManager.instance = new PoolManager();
    }
    return PoolManager.instance;
  }

  // Register a new pool
  registerPool<T>(
    name: string,
    createFn: () => T,
    resetFn: (obj: T) => void,
    maxSize: number = 50,
    activeFn?: (obj: T) => void
  ): void {
    const pool = new ObjectPool<T>(createFn, resetFn, maxSize, activeFn);
    this.pools.set(name, pool);
  }

  // Get object from named pool
  get<T>(poolName: string): T | null {
    const pool = this.pools.get(poolName);
    return pool ? pool.get() : null;
  }

  // Release object to named pool
  release(poolName: string, obj: any): void {
    const pool = this.pools.get(poolName);
    if (pool) {
      pool.release(obj);
    }
  }

  // Preload a pool
  preload(poolName: string, count: number): void {
    const pool = this.pools.get(poolName);
    if (pool) {
      pool.preload(count);
    }
  }

  // Get pool stats
  getPoolStats(poolName: string): any {
    const pool = this.pools.get(poolName);
    return pool ? pool.getStats() : null;
  }

  // Get all pool stats
  getAllStats(): { [poolName: string]: any } {
    const stats: { [poolName: string]: any } = {};
    this.pools.forEach((pool, name) => {
      stats[name] = pool.getStats();
    });
    return stats;
  }

  // Clear all pools
  clearAllPools(): void {
    this.pools.forEach(pool => pool.clear());
  }

  // Initialize common game object pools
  initializeGamePools(scene: Phaser.Scene): void {
    // Projectile pool
    this.registerPool(
      'projectiles',
      () => scene.add.circle(0, 0, 3, 0xffff00).setVisible(false),
      (obj: Phaser.GameObjects.Arc) => {
        obj.setVisible(false);
        obj.setPosition(0, 0);
        obj.setRotation(0);
        obj.setScale(1);
        obj.setAlpha(1);
        if (obj.body && 'setVelocity' in obj.body) {
          obj.body.setVelocity(0, 0);
        }
      },
      50,
      (obj) => {
        obj.setVisible(true);
        obj.setActive(true);
      }
    );

    // Particle effect pool
    this.registerPool(
      'particles',
      () => scene.add.circle(0, 0, 2, 0xff6600).setVisible(false),
      (obj: Phaser.GameObjects.Arc) => {
        obj.setVisible(false);
        obj.setPosition(0, 0);
        obj.setScale(1);
        obj.setAlpha(1);
        obj.setFillStyle(0xffffff);
      },
      30,
      (obj) => {
        obj.setVisible(true);
        obj.setActive(true);
      }
    );

    // Damage number pool
    this.registerPool(
      'damageNumbers',
      () => scene.add.text(0, 0, '', {
        fontSize: '16px',
        color: '#ff0000',
        fontFamily: 'Arial',
        stroke: '#000000',
        strokeThickness: 2,
      }).setVisible(false),
      (obj) => {
        obj.setVisible(false);
        obj.setPosition(0, 0);
        obj.setAlpha(1);
        obj.setScale(1);
        obj.setText('');
        obj.setStyle({ color: '#ff0000' });
      },
      20,
      (obj) => {
        obj.setVisible(true);
        obj.setActive(true);
      }
    );

    // Explosion effect pool
    this.registerPool(
      'explosions',
      () => {
        const explosion = scene.add.graphics().setVisible(false);
        return explosion;
      },
      (obj) => {
        obj.setVisible(false);
        obj.setPosition(0, 0);
        obj.setAlpha(1);
        obj.setScale(1);
        obj.clear();
      },
      15,
      (obj) => {
        obj.setVisible(true);
        obj.setActive(true);
      }
    );

    console.log('Game object pools initialized');
  }

  // Performance monitoring
  logPerformanceStats(): void {
    const stats = this.getAllStats();
    console.log('Object Pool Performance Stats:', stats);
    
    // Calculate total objects created vs reused
    let totalCreated = 0;
    let totalInPools = 0;
    Object.values(stats).forEach((poolStats: any) => {
      totalCreated += poolStats.totalCreated;
      totalInPools += poolStats.poolSize;
    });
    
    const reuseRatio = totalInPools / Math.max(totalCreated, 1);
    console.log(`Object Reuse Ratio: ${(reuseRatio * 100).toFixed(1)}%`);
  }
}

// Utility functions for easy access
export const getPool = (name: string) => PoolManager.getInstance().get(name);
export const releaseToPool = (poolName: string, obj: any) => PoolManager.getInstance().release(poolName, obj);
export const preloadPool = (poolName: string, count: number) => PoolManager.getInstance().preload(poolName, count); 